<template>
  <div class="credential-form">
    <!-- Username -->
    <div class="form-group mb-3">
      <label class="form-label">Username</label>
      <div class="input-wrapper">
        <span class="input-icon">
          <i class="fas fa-user"></i>
        </span>
        <input v-model="username" type="text" placeholder="Enter your username" class="form-control input-field" :class="{ 'border-danger is-invalid': errorMessage.username }
          " @keyup="inputVal('username')" @keyup.enter.prevent="loginUser" />
      </div>
      <small v-if="errorMessage.username" class="text-danger text-xs mt-1 d-block">
        {{ errorMessage.username }}
      </small>
    </div>

    <!-- Password -->
    <div class="form-group mb-3">
      <label class="form-label">Password</label>
      <div class="input-wrapper">
        <span class="input-icon">
          <i class="fas fa-lock"></i>
        </span>
        <input v-model="password" :type="showPassword ? 'text' : 'password'" placeholder="Enter your password"
          class="form-control input-field" :class="{ 'border-danger is-invalid': errorMessage.password }
            " @keyup="inputVal('password')" @keyup.enter.prevent="loginUser" />
        <button type="button" class="password-toggle" tabindex="-1"
          :aria-label="showPassword ? 'Hide password' : 'Show password'" @click="showPassword = !showPassword">
          <i class="fas" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
        </button>
      </div>
      <small v-if="errorMessage.password" class="text-danger text-xs mt-1 d-block">
        {{ errorMessage.password }}
      </small>
      <a href="/forgot-password" class="forgot-link">Forgot Password</a>
    </div>

    <!-- CAPTCHA -->
    <div class="mb-3">
      <div class="captcha-wrapper">
        <span class="captcha-label">
          <i class="fas fa-shield-alt me-1"></i>Security Verification
        </span>
        <div class="captcha-body">
          <div class="captcha-top">
            <div class="captcha-image-box">
              <div v-if="captchaLoading" class="captcha-skeleton"></div>
              <img v-else-if="captchaImageUrl" :src="captchaImageUrl" alt="CAPTCHA" class="captcha-image" />
              <div v-else class="captcha-placeholder">
                <i class="fas fa-spinner fa-spin"></i>
              </div>
            </div>
            <button type="button" class="captcha-refresh" title="Refresh CAPTCHA" :disabled="captchaLoading"
              @click="refreshCaptcha">
              <i class="fas fa-sync"></i>
            </button>
          </div>
          <input v-model="captchaText" type="text" placeholder="Enter the text from the image"
            class="form-control captcha-input" :class="{ 'border-danger is-invalid': errorMessage.captcha }"
            @keyup="inputVal('captcha')" @keyup.enter.prevent="loginUser" />
        </div>
      </div>
      <small v-if="errorMessage.captcha" class="text-danger text-xs mt-1 d-block">
        {{ errorMessage.captcha }}
      </small>
    </div>

    <!-- Submit -->
    <div class="mt-3">
      <button class="btn-submit" :disabled="onLoading" @click.prevent="loginUser">
        <span v-if="onLoading">
          <i class="fas fa-spinner fa-spin me-2"></i>Signing in...
        </span>
        <span v-else>
          <i class="fas fa-arrow-right-to-bracket me-2"></i>Sign In
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import authService from "@/services/authService";
import baseApiUrl from "@/configs/config";
import { getDeviceId } from "@/helpers/deviceId";
import menuService from "@/services/generalSettings/menuService";

export default {
  name: "LoginCredential",
  data() {
    return {
      username: "",
      password: "",
      showPassword: false,
      errorMessage: {
        username: "",
        password: "",
        both: "",
        captcha: "",
      },
      onLoading: false,
      captchaText: "",
      captchaImageUrl: "",
      captchaKey: "",
      captchaLoading: false,
    };
  },
  mounted() {
    this.fetchCaptcha();
  },
  methods: {
    async fetchCaptcha() {
      this.captchaLoading = true;
      try {
        const response = await fetch(`${baseApiUrl.apiUrl}/auth/captcha/default`);
        const data = await response.json();
        this.captchaImageUrl = data.img;
        this.captchaKey = data.key;
        this.captchaText = "";
      } catch (err) {
        console.error("Failed to fetch CAPTCHA:", err);
      } finally {
        this.captchaLoading = false;
      }
    },
    refreshCaptcha() {
      this.fetchCaptcha();
    },
    async loginUser() {
      if (this.username.trim() === "" || this.password.trim() === "") {
        if (this.username.trim() === "") {
          this.errorMessage.username = "Username is required";
        }
        if (this.password.trim() === "") {
          this.errorMessage.password = "Password is required";
        }
        this.errorMessage.both = "Username and Password is required";
        return false;
      }
      if (!this.captchaText.trim()) {
        this.errorMessage.captcha = "Please enter the CAPTCHA text";
        return false;
      }

      this.onLoading = true;

      try {
        const response = await authService.login({
          username: this.username,
          password: this.password,
          captcha: this.captchaText,
          _captcha_key: this.captchaKey,
        });
        const resData = response.data.data;
        let payload = resData.user || {};
        if (!payload.id && resData.token) {
          try {
            payload = JSON.parse(
              atob(resData.token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
            );
          } catch {
            payload = {};
          }
        }

        this.$store.dispatch("auth/setUser", {
          id: payload.id || payload.sub,
          namecode: payload.namecode,
          name: payload.name,
          username: payload.username,
          client_code: payload.client_code,
          company_code: payload.company_code,
        });

        const roles = resData.roles || [];
        const permissions = resData.permissions || [];
        this.$store.dispatch("auth/setUserRolesPermissions", { roles, permissions });

        this.$store.dispatch("auth/login", resData);

        // Cek apakah device ini sudah terdaftar/aktif di perangkat lain
        const deviceRegistered = await this.handleDeviceSession();
        if (!deviceRegistered) return; // User membatalkan login

        await this.setMenuSubMenu();

        this.errorMessage = {
          username: "",
          password: "",
          both: "",
        };

        this.$store.commit("auth/setSelectedModule", "Material Management");
        window.location = this.getRedirectTarget();
      } catch (error) {
        // Reset semua pesan error dulu agar tidak ada error lama yang menempel
        this.errorMessage = {
          username: "",
          password: "",
          both: "",
          captcha: "",
        };

        // Backend validation error berbentuk:
        // { message: "Validation failed", data: { captcha: ["The captcha is invalid or has expired."] } }
        const errorData = error.response?.data?.data;
        const fieldErrors =
          errorData && typeof errorData === "object" && !Array.isArray(errorData)
            ? errorData
            : null;

        if (fieldErrors) {
          if (Array.isArray(fieldErrors.captcha) && fieldErrors.captcha.length) {
            this.errorMessage.captcha = fieldErrors.captcha[0];
          }
          if (Array.isArray(fieldErrors.username) && fieldErrors.username.length) {
            this.errorMessage.username = fieldErrors.username[0];
          }
          if (Array.isArray(fieldErrors.password) && fieldErrors.password.length) {
            this.errorMessage.password = fieldErrors.password[0];
          }
          // Hanya tampilkan hint "username/password salah" jika kredensial yang benar-benar gagal
          if (fieldErrors.username || fieldErrors.password) {
            this.errorMessage.both = "The password or username may be incorrect";
          }
        } else if (error.response?.data?.message === "CAPTCHA verification failed.") {
          this.errorMessage.captcha = "CAPTCHA verification failed. Please try again.";
        } else {
          this.errorMessage.username = "The entered username may be incorrect";
          this.errorMessage.password = "The password may be incorrect";
          this.errorMessage.both = "The password or username may be incorrect";
        }

        this.onLoading = false;
        await this.refreshCaptcha();
      }
    },
    getRedirectTarget() {
      const redirect = this.$route.query.redirect;
      const isSafeRedirect = typeof redirect === "string" && redirect.startsWith("/") && !redirect.startsWith("//") && !redirect.includes("://");
      return isSafeRedirect ? redirect : "/material-management/dashboards/my-task";
    },
    inputVal(val) {
      if (val === "username") {
        this.errorMessage.username = "";
      } else if (val === "password") {
        this.errorMessage.password = "";
      } else if (val === "captcha") {
        this.errorMessage.captcha = "";
      }

      if (this.errorMessage.username === "" && this.errorMessage.password === "") {
        this.errorMessage.both = "";
      }
    },
    async handleDeviceSession() {
      try {
        const deviceRes = await authService.deviceLogin();
        const deviceData = deviceRes.data.data;
        const myDeviceId = getDeviceId();

        // data berupa array kosong = tidak ada device terdaftar, langsung lanjut
        if (Array.isArray(deviceData) && deviceData.length === 0) {
          return true;
        }

        // data berupa object map { device_id: { ... }, ... }
        if (!deviceData || typeof deviceData !== "object") {
          return true;
        }

        const deviceCount = Object.keys(deviceData).length;

        // Hanya 1 device = device kita sendiri yang baru login, tidak ada konflik
        if (deviceCount <= 1) {
          return true;
        }

        // Lebih dari 1 device = ada device lain yang masih aktif, tawarkan revoke
        const result = await this.confirmDialog(
          "You are already logged in on another session. Do you want to revoke the previous session and continue?",
          "revoke & continue"
        );

        if (!result.isConfirmed) {
          // User membatalkan — logout dari store
          this.$store.dispatch("auth/logout");
          this.onLoading = false;
          await this.refreshCaptcha();
          return false;
        }

        // Revoke semua sesi lama kecuali device kita, lalu bind ulang
        for (const deviceId of Object.keys(deviceData)) {
          if (deviceId !== myDeviceId) {
            try {
              await authService.revokeDevice(deviceId);
            } catch {
              // Abaikan error revoke
            }
          }
        }
        await authService.deviceLogin();
        return true;
      } catch (deviceErr) {
        console.error("Failed device session check:", deviceErr);
        return true;
      }
    },
    async setMenuSubMenu() {
      try {
        const responseMenu = await menuService.getManageMenu();
        const menus = responseMenu.data.data;
        const menuNav = menus.reduce((acc, menu) => {
          acc[menu.name] = { status: menu.status === "active" };
          return acc;
        }, {});
        const subMenuNav = menus.reduce((acc, menu) => {
          menu.sub_menu.forEach((sub) => {
            acc[sub.name] = { status: sub.status === "active" };
          });
          return acc;
        }, {});

        this.$store.dispatch("auth/setMenuNav", menuNav);
        this.$store.dispatch("auth/setSubMenuNav", subMenuNav);
      } catch {
        this.$store.dispatch("auth/setMenuNav", []);
        this.$store.dispatch("auth/setSubMenuNav", []);
      }
    },
  },
};
</script>

<style scoped>
.credential-form {
  animation: fadeIn 0.35s ease-out;
}

/* ============================================
   FORM LABEL
   ============================================ */
.form-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #344767;
  margin-bottom: 0.4rem;
  display: block;
  letter-spacing: 0.3px;
}

.forgot-link {
  font-size: 0.72rem;
  font-weight: 600;
  color: #5e72e4;
  text-decoration: none;
  transition: color 0.2s ease;
}

.forgot-link:hover {
  color: #324cdd;
  text-decoration: underline;
}

/* ============================================
   INPUT WRAPPER
   ============================================ */
.form-group {
  position: relative;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  font-size: 0.85rem;
  pointer-events: none;
  z-index: 2;
  transition: color 0.2s ease;
}

.input-wrapper:focus-within .input-icon {
  color: #5e72e4;
}

.input-field {
  width: 100%;
  padding: 0.7rem 1rem 0.7rem 2.5rem;
  font-size: 0.88rem;
  color: #32325d;
  background: #f8f9fe;
  border: 1.5px solid #e9ecef;
  border-radius: 0.75rem;
  transition: all 0.2s ease;
  outline: none;
}

.input-field::placeholder {
  color: #adb5bd;
  font-weight: 400;
}

.input-field:focus {
  background: #ffffff;
  border-color: #5e72e4;
  box-shadow: 0 0 0 3px rgba(94, 114, 228, 0.1);
}

.input-field.is-invalid {
  border-color: #f5365c;
  background: #fff5f7;
}

.input-field.is-invalid:focus {
  box-shadow: 0 0 0 3px rgba(245, 54, 92, 0.1);
}

/* ============================================
   PASSWORD TOGGLE
   ============================================ */
.password-toggle {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  background: transparent;
  color: #adb5bd;
  cursor: pointer;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  z-index: 2;
}

.password-toggle:hover {
  color: #5e72e4;
  background: rgba(94, 114, 228, 0.08);
}

/* ============================================
   ERROR ALERT
   ============================================ */
.alert-danger {
  background: #fff5f7;
  border: 1px solid #fccacf;
  border-radius: 0.75rem;
  color: #f5365c;
}

/* ============================================
   CAPTCHA
   ============================================ */
.captcha-wrapper {
  border: 1.5px solid #e9ecef;
  border-radius: 0.75rem;
  background: #f8f9fe;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.captcha-label {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #8898aa;
  display: flex;
  align-items: center;
}

.captcha-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.captcha-top {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.captcha-image-box {
  flex-shrink: 0;
  width: 200px;
  height: 48px;
  border: 1.5px solid #e9ecef;
  border-radius: 0.5rem;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.captcha-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.captcha-placeholder {
  color: #adb5bd;
  font-size: 1.1rem;
}

.captcha-skeleton {
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #e9ecef 25%, #f1f3f5 50%, #e9ecef 75%);
  background-size: 200% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
  border-radius: 0.25rem;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

.captcha-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.captcha-refresh {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border: 1.5px solid #e9ecef;
  border-radius: 0.5rem;
  background: #ffffff;
  color: #8898aa;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  transition: all 0.2s ease;
}

.captcha-refresh:hover {
  color: #5e72e4;
  border-color: #5e72e4;
}

.captcha-input {
  width: 100%;
  padding: 0.55rem 0.75rem;
  font-size: 0.85rem;
}

/* ============================================
   SUBMIT BUTTON
   ============================================ */
.btn-submit {
  width: 100%;
  padding: 0.75rem 1.5rem;
  font-size: 0.88rem;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(87deg, #5e72e4 0%, #825ee4 100%);
  border: none;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(94, 114, 228, 0.3);
  position: relative;
  overflow: hidden;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(94, 114, 228, 0.4);
}

.btn-submit:active:not(:disabled) {
  transform: translateY(0);
}

.btn-submit:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* Ripple effect */
.btn-submit::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.1);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.btn-submit:hover:not(:disabled)::after {
  opacity: 1;
}

/* ============================================
   ANIMATIONS
   ============================================ */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
